import { z } from "zod";

/**
 * One slide (ported from the old engine's render/spec.ts). Every field is present and nullable
 * (structured outputs need a fixed shape), and `layout` decides which fields are used.
 */
export const slideSchema = z.object({
  layout: z
    .enum(["cover", "point", "markup", "compare", "checklist", "quote", "cta"])
    .describe(
      "cover: the hook slide. point: one idea with a number. markup: an example marked keep/change. compare: weak vs strong. checklist: up to 5 short items. quote: one illustrative example. cta: the last slide.",
    ),
  eyebrow: z
    .string()
    .nullable()
    .describe("Short label above the title, 1–4 words (cover, point, checklist)"),
  title: z
    .string()
    .nullable()
    .describe("cover: the hook, max ~12 words. cta: the closing line, max ~8 words"),
  subtitle: z.string().nullable().describe("cover: one supporting line, max ~18 words"),
  heading: z.string().nullable().describe("point, compare, checklist, markup: max ~10 words"),
  body: z.string().nullable().describe("point, cta: max ~40 words"),
  question: z.string().nullable().describe("markup: the question or situation being answered"),
  sentences: z
    .array(z.object({ text: z.string(), role: z.enum(["keep", "change", "plain"]) }))
    .nullable()
    .describe(
      "markup: 2–4 sentences of one illustrative example; mark exactly one keep and one change",
    ),
  keepNote: z.string().nullable().describe("markup: why the keep sentence works, max ~10 words"),
  changeNote: z.string().nullable().describe("markup: what to change, max ~10 words"),
  weak: z.string().nullable().describe("compare: the weak version, max ~30 words"),
  strong: z.string().nullable().describe("compare: the strong version, max ~40 words"),
  items: z.array(z.string()).nullable().describe("checklist: 3–5 items, max ~12 words each"),
  quote: z.string().nullable().describe("quote: an illustrative example, max ~45 words"),
  attribution: z.string().nullable().describe("quote: e.g. 'Illustrative example'"),
  action: z.string().nullable().describe("cta: the button text, max ~6 words"),
});

export type SlideInput = z.infer<typeof slideSchema>;

/** Channels that take a carousel: an Instagram carousel, or a LinkedIn document (PDF). */
export const carouselChannelSchema = z.enum([
  "instagram",
  "linkedin",
  "tiktok",
  "snapchat",
  "facebook",
]);
export type CarouselChannelName = z.infer<typeof carouselChannelSchema>;

export const pieceFormatSchema = z.enum(["text", "carousel"]);
export type PieceFormat = z.infer<typeof pieceFormatSchema>;
